import { formatCheckAmount } from "./amountInWords";

export const PRIZE_LINE_COLUMN_KEYS = [
  "eventName",
  "place",
  "squadName",
  "bowlingCenterName",
  "teamName",
  "amount",
];

export const PRIZE_LINE_COLUMN_OPTIONS = [
  { key: "eventName", label: "Event", required: false },
  { key: "place", label: "Place", required: false },
  { key: "squadName", label: "Squad", required: false },
  { key: "bowlingCenterName", label: "Bowling center", required: false },
  { key: "teamName", label: "Team name", required: false },
  { key: "amount", label: "Amount", required: true },
];

export const DEFAULT_PRIZE_LINE_COLUMNS = {
  eventName: true,
  place: true,
  amount: true,
  squadName: false,
  bowlingCenterName: false,
  teamName: false,
};

export const SAMPLE_PRIZE_LINES = [
  {
    eventName: "Singles",
    place: "1st",
    amount: 250,
    squadName: "Saturday 9:00 AM",
    bowlingCenterName: "Sunset Lanes",
    teamName: "",
  },
  {
    eventName: "Doubles",
    place: "2nd",
    amount: 75,
    squadName: "Saturday 1:00 PM",
    bowlingCenterName: "Sunset Lanes",
    teamName: "Smith / Jones",
  },
  {
    eventName: "All Events",
    place: "3rd",
    amount: 50,
    squadName: "Sunday 10:00 AM",
    bowlingCenterName: "Sunset Lanes",
    teamName: "",
  },
];

export const SAMPLE_PRIZE_TOTAL = SAMPLE_PRIZE_LINES.reduce(
  (sum, prize) => sum + Number(prize.amount || 0),
  0
);

export const normalizePrizeLineColumns = (columns = {}) => ({
  eventName: columns.eventName !== false,
  place: columns.place !== false,
  amount: true,
  squadName: Boolean(columns.squadName),
  bowlingCenterName: Boolean(columns.bowlingCenterName),
  teamName: Boolean(columns.teamName),
});

export const formatSquadLabel = (prize = {}) => {
  if (prize.squadName) return String(prize.squadName);
  if (prize.squadDescription) return String(prize.squadDescription);
  return [prize.squadDate, prize.squadTime].filter(Boolean).join(" ");
};

const columnValue = (prize, key, formatAmount) => {
  if (key === "amount") return formatAmount(prize.amount);
  if (key === "squadName") return formatSquadLabel(prize);
  if (key === "bowlingCenterName") {
    return prize.bowlingCenterName || "";
  }
  if (key === "eventName") return prize.eventName || "";
  if (key === "place") return prize.place || "";
  if (key === "teamName") return prize.teamName || "";
  return "";
};

export const formatPrizeLine = (
  prize,
  columns = DEFAULT_PRIZE_LINE_COLUMNS,
  formatAmount = formatCheckAmount
) => {
  const enabled = normalizePrizeLineColumns(columns);
  return PRIZE_LINE_COLUMN_KEYS.map((key) =>
    enabled[key] ? columnValue(prize, key, formatAmount) : ""
  )
    .filter(Boolean)
    .join(" — ");
};

export const maxPrizeLinesForBox = (heightInches, fontSizePt) => {
  const size = Number(fontSizePt) || 12;
  const heightPt = Math.max(0, Number(heightInches) || 0) * 72;
  const lineHeight = size * 1.15;
  return Math.max(2, Math.floor(heightPt / lineHeight) || 2);
};

export const formatPrizeLineBlock = ({
  prizes = [],
  columns = DEFAULT_PRIZE_LINE_COLUMNS,
  totalAmount = 0,
  formatAmount = formatCheckAmount,
  maxRows = 12,
}) => {
  const enabled = normalizePrizeLineColumns(columns);
  const body = (Array.isArray(prizes) ? prizes : [])
    .map((prize) => formatPrizeLine(prize, enabled, formatAmount))
    .filter(Boolean);
  const totalLine = `Total — ${formatAmount(totalAmount)}`;
  const limit = Math.max(2, Number(maxRows) || 2);
  if (body.length + 1 <= limit) return [...body, totalLine];
  const kept = Math.max(0, limit - 2);
  const hidden = body.length - kept;
  return [...body.slice(0, kept), `+ ${hidden} more`, totalLine];
};

const COLUMN_WEIGHTS = {
  eventName: 2.2,
  place: 0.9,
  squadName: 1.4,
  bowlingCenterName: 1.5,
  teamName: 1.4,
  amount: 1.1,
};

export const prizeLineColumnList = (columns) => {
  const enabled = normalizePrizeLineColumns(columns);
  return PRIZE_LINE_COLUMN_OPTIONS.filter((column) => enabled[column.key]).map(
    (column) => ({
      key: column.key,
      label: column.label,
      align: column.key === "amount" ? "right" : "left",
    })
  );
};

export const prizeLinesTable = ({
  prizes = [],
  columns = DEFAULT_PRIZE_LINE_COLUMNS,
  totalAmount = 0,
  formatAmount = formatCheckAmount,
  maxRows = 12,
} = {}) => {
  const cols = prizeLineColumnList(columns);
  const all = Array.isArray(prizes) ? prizes : [];
  const lineBudget = Math.max(3, Number(maxRows) || 3);
  const bodyBudget = Math.max(1, lineBudget - 2);
  let visible = all;
  let moreCount = 0;
  if (all.length > bodyBudget) {
    const kept = Math.max(0, bodyBudget - 1);
    visible = all.slice(0, kept);
    moreCount = all.length - kept;
  }
  return {
    columns: cols,
    rows: visible.map((prize) =>
      cols.map((column) => columnValue(prize, column.key, formatAmount))
    ),
    moreLabel: moreCount > 0 ? `+ ${moreCount} more` : "",
    totalLabel: "Total",
    totalAmount: formatAmount(totalAmount),
  };
};

export const prizeLineColumnWidths = (columns, totalWidth) => {
  const cols = prizeLineColumnList(columns);
  const weights = cols.map((column) => COLUMN_WEIGHTS[column.key] || 1);
  const sum = weights.reduce((total, weight) => total + weight, 0) || 1;
  const width = Number(totalWidth) || 0;
  return cols.map((column, index) => ({
    ...column,
    width: (weights[index] / sum) * width,
  }));
};

const totalFromValues = (values, prizes) => {
  if (values.amount != null && values.amount !== "") {
    const parsed = Number(String(values.amount).replace(/[^0-9.]/g, ""));
    if (Number.isFinite(parsed)) return parsed;
  }
  return prizes.reduce((sum, prize) => sum + Number(prize.amount || 0), 0);
};

export const prizeLinesTableForField = (field, values = {}) => {
  const prizes = Array.isArray(values.prizes)
    ? values.prizes
    : SAMPLE_PRIZE_LINES;
  return prizeLinesTable({
    prizes,
    columns: field?.prizeLineColumns,
    totalAmount: totalFromValues(values, prizes),
    maxRows: maxPrizeLinesForBox(field?.heightInches, field?.fontSizePt),
  });
};

export const prizeLinesFieldText = (field, values = {}) => {
  const table = prizeLinesTableForField(field, values);
  const header = table.columns.map((column) => column.label).join("  ");
  const body = table.rows.map((cells) => cells.join("  "));
  const lines = [header, ...body];
  if (table.moreLabel) lines.push(table.moreLabel);
  lines.push(`${table.totalLabel}  ${table.totalAmount}`);
  return lines.join("\n");
};
